import React, { useEffect, useMemo, useRef, useState } from "react";
import Table, { Column } from "../../components/Table";
import Modal from "../../components/Modal";
import FormBuilder, { FieldConfig } from "../../components/FormBuilder";
import InputField from "../../components/InputField";
import Button from "../../components/Button";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  getManageAllVehicles,
  searchManageVehicles,
  ManageCreateVehicles,
  ManageUpdateVehicles,
  ManageDeleteVehicles,
  extractApiErrors,
} from "../../services/vehicleService";

// Vehicle form fields configuration
const vehicleTypes = [
  "Two_wheeler",
  "Auto",
  "Car",
  "Truck",
  "Bus",
  "Tractor",
  "Commercial",
  "Other",
];

const indianManufacturers = [
  // Two Wheelers
  "Hero",
  "Honda",
  "TVS",
  "Bajaj",
  "Royal Enfield",
  "Yamaha",
  "Suzuki",
  "KTM",
  "Ola Electric",
  "Ather",
  "Ampere",
  "Revolt",

  // Cars
  "Maruti Suzuki",
  "Hyundai",
  "Tata",
  "Mahindra",
  "Kia",
  "Toyota",
  "Honda Cars",
  "Renault",
  "Nissan",
  "Skoda",
  "Volkswagen",
  "MG",
  "Jeep",
  "BMW",
  "Mercedes-Benz",
  "Audi",
  "Volvo",
  "Lexus",

  // Commercial / Trucks / Buses
  "Ashok Leyland",
  "Eicher",
  "BharatBenz",
  "Force Motors",
  "SML Isuzu",
  "VE Commercial Vehicles",

  // Tractor
  "Swaraj",
  "Sonalika",
  "John Deere",
  "New Holland",
  "Escorts Kubota",
  "Massey Ferguson",

  // Auto
  "Piaggio",
  "Atul Auto",

  "Other",
];

const vehicleFormFields: FieldConfig[] = [
  {
    name: "vehicle_type",
    label: "Vehicle Type",
    type: "chips",
    required: true,
    options: vehicleTypes,
  },
  {
    name: "manufacturer",
    label: "Manufacturer",
    type: "text",
    required: true,
    placeholder: "Enter manufacturer name",
  },
  {
    name: "model",
    label: "Model",
    type: "text",
    required: true,
    placeholder: "Enter model name",
  },
  // {
  //   name: "variant",
  //   label: "Color",
  //   type: "text",
  //   required: true,
  //   placeholder: "Enter Color",
  // },
  // {
  //   name: "year_of_manufacture",
  //   label: "Year of Manufacture",
  //   type: "number",
  //   required: true,
  //   placeholder: "Enter year",
  // },
];

type VehicleRow = {
  id: string | number;
  vehicle_type: string;
  manufacturer?: string | null;
  model: string;
  variant: string;
  year_of_manufacture: string | number;
  raw?: any;
};

const VehicleManagement = () => {
  const [data, setData] = useState<VehicleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string[]> | null>(
    null,
  );
  const formRef = useRef<any>(null);
  const [editingVehicle, setEditingVehicle] = useState<VehicleRow | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<VehicleRow | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  // Server pagination state
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  // Search state
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const debounceRef = useRef<number | null>(null);
  const [selectedCount, setSelectedCount] = useState<number | null>(null);

  // Debounce searchTerm -> debouncedSearch after 500ms
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 500);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  // whenever debouncedSearch changes, reset to page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Normalize vehicle data
  const normalizeVehicle = (vehicle: any): VehicleRow => {
    const resolvedId = vehicle.id ?? vehicle.vehicle_id ?? null;

    return {
      id: resolvedId,
      vehicle_type: vehicle.vehicle_type || "Unknown",
      manufacturer: vehicle.manufacturer || null,
      model: vehicle.model || "N/A",
      variant: vehicle.variant || "",
      year_of_manufacture: vehicle.year_of_manufacture || "",
      raw: vehicle,
    } as VehicleRow;
  };

  // Fetch vehicles with server-side pagination and search
  // Fetch vehicles with server-side pagination and search
  const fetchVehicles = async (
    p = page,
    per_page = pageSize,
    query = debouncedSearch,
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Prepare parameters for the API
      const params: any = {
        page: p,
        per_page,
      };

      // Add search parameter only if query exists
      if (query && query.length > 0) {
        params.search = query;
      }

      // Call the service (using getManageAllVehicles for both now is cleaner)
      const res = await getManageAllVehicles(params);
      const payload = res ?? {};

      /**
       * SUCCESSFUL DATA MAPPING
       * Based on your JSON:
       * payload.data -> The pagination object
       * payload.data.data -> The actual Array of vehicles
       */
      const paginationRoot = payload.data || {};
      const rawList = Array.isArray(paginationRoot.data)
        ? paginationRoot.data
        : [];

      const items = rawList.map(normalizeVehicle);

      // Sync metadata from the nested pagination object
      setData(items);
      setTotal(paginationRoot.total ?? 0);
      setPage(paginationRoot.current_page ?? p);
      setPageSize(paginationRoot.per_page ?? per_page);
      setLastPage(paginationRoot.last_page ?? 1);
      setFrom(paginationRoot.from ?? 0);
      setTo(paginationRoot.to ?? 0);
      setSelectedCount(paginationRoot.total ?? 0);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch vehicles");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when pagination/search changes
  useEffect(() => {
    fetchVehicles(page, pageSize, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch]);

  // Handle search key down
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      setDebouncedSearch(searchTerm.trim());
    }
  };

  // Get form fields
  const getVehicleFields = (isEdit: boolean): FieldConfig[] => {
    return vehicleFormFields.map((field) => {
      if (isEdit && "name" in field && field.name === "vehicle_type") {
        return { ...field, readOnly: false };
      }
      return field;
    });
  };

  // Handle Create
  const handleCreateVehicle = async (formData: any) => {
    setFormErrors(null);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (value instanceof FileList && value.length > 0) {
          data.append(key, value[0]);
        } else if (value instanceof File) {
          data.append(key, value);
        } else if (
          Array.isArray(value) &&
          value.length > 0 &&
          value[0] instanceof File
        ) {
          data.append(key, value[0]);
        } else if (typeof value === "object") {
          data.append(key, JSON.stringify(value));
        } else {
          data.append(key, value.toString());
        }
      });

      const res = await ManageCreateVehicles(data);
      toast.success(res.message || "Vehicle created successfully!");
      setOpenModal(false);
      setEditingVehicle(null);
      setPage(1);
      await fetchVehicles(1, pageSize, debouncedSearch);
      formRef.current?.reset();
    } catch (err: any) {
      const errors = extractApiErrors(err);
      setFormErrors(errors);
      const messages = [
        err?.response?.data?.message,
        ...Object.values(errors).flat(),
      ]
        .filter(Boolean)
        .join(", ");
      toast.error(messages || "Failed to create vehicle");
      console.error(err);
    }
  };

  // Handle Update
  const handleUpdateVehicle = async (formData: any) => {
    if (!editingVehicle) return;
    setFormErrors(null);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (value instanceof FileList && value.length > 0) {
          data.append(key, value[0]);
        } else if (value instanceof File) {
          data.append(key, value);
        } else if (
          Array.isArray(value) &&
          value.length > 0 &&
          value[0] instanceof File
        ) {
          data.append(key, value[0]);
        } else if (typeof value === "object") {
          data.append(key, JSON.stringify(value));
        } else {
          data.append(key, value.toString());
        }
      });

      const res = await ManageUpdateVehicles(editingVehicle.id, data);
      toast.success(res.message || "Vehicle updated successfully!");
      setOpenModal(false);
      setEditingVehicle(null);
      await fetchVehicles(page, pageSize, debouncedSearch);
    } catch (err: any) {
      const errors = extractApiErrors(err);
      setFormErrors(errors);
      const messages = [
        err?.response?.data?.message,
        ...Object.values(errors).flat(),
      ]
        .filter(Boolean)
        .join(", ");
      toast.error(messages || "Failed to update vehicle");
      console.error(err);
    }
  };

  // Handle Delete
  const handleDeleteClick = (row: VehicleRow) => {
    setVehicleToDelete(row);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!vehicleToDelete) return;
    setDeleting(true);
    try {
      const res = await ManageDeleteVehicles(vehicleToDelete.id);
      toast.success(res.message || "Vehicle deleted successfully!");
      setDeleteModalOpen(false);
      setVehicleToDelete(null);
      await fetchVehicles(page, pageSize, debouncedSearch);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to delete vehicle");
    } finally {
      setDeleting(false);
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    setFormErrors(null);
    if (formRef.current) {
      (formRef.current as any).reset();
      if ((formRef.current as any).clearErrors) {
        (formRef.current as any).clearErrors();
      }
    }
    setOpenModal(false);
    setEditingVehicle(null);
  };

  const buildDefaultValues = (fields: FieldConfig[]) => {
    const defaults: any = {};
    fields.forEach((field) => {
      if ("name" in field && field.defaultValue !== undefined) {
        defaults[field.name] = field.defaultValue;
      }
    });
    return defaults;
  };

  const columns: Column<VehicleRow>[] = useMemo(
    () => [
      {
        header: "Vehicle Type",
        accessor: "vehicle_type",
        sortable: true,
        cell: (row) => (
          <div className="flex items-center gap-3">
            <span className="font-semibold">{row?.vehicle_type}</span>
          </div>
        ),
      },
      {
        header: "Manufacturer",
        accessor: "manufacturer",
        sortable: true,
        cell: (row) => (
          <div className="font-mono text-sm">{row?.manufacturer || "—"}</div>
        ),
      },
      {
        header: "Model",
        accessor: "model",
        sortable: true,
        cell: (row) => <div>{row?.model || "—"}</div>,
      },
      // {
      //   header: "Color",
      //   accessor: "variant",
      //   sortable: true,
      //   cell: (row) => <div>{row?.variant || "—"}</div>,
      // },
      // {
      //   header: "Year of Manufacture",
      //   accessor: "year_of_manufacture",
      //   sortable: true,
      //   cell: (row) => <div>{row?.year_of_manufacture || "—"}</div>,
      // },
    ],
    [],
  );

  if (error && data.length === 0)
    return <div className="p-4 text-red-500">Error: {error}</div>;

  return (
    <div className="pt-3">
      <div className="grid grid-cols-1 md:grid-cols-2 mb-4 gap-4 items-center light-bg-color p-4 rounded-xl shadow-sm">
        <div className="flex justify-between">
          <h2 className="text-2xl font-semibold text-color">Vehicles</h2>

          <div className="flex items-center gap-2">
            {selectedCount !== null && (
              <span
                className="
      text-xs 
      font-medium 
      text-white 
      px-3 
      py-1 
      rounded-full 
      shadow-sm 
      flex 
      items-center 
      gap-1
      transition 
      bg-yellow-500
    "
              >
                <span className="">count:</span>
                {selectedCount}
              </span>
            )}
          </div>
        </div>

        <div className="">
          {/* Search Input */}
          <div className="relative w-full">
            <InputField
              placeholder="Search (type, manufacturer, model, variant, year...)"
              className="w-full focus:outline-none focus:ring-2 focus:ring-sky-300 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon icon="mdi:magnify" width={18} className="text-slate-400" />
            </div>
          </div>
        </div>
      </div>

      <Table<VehicleRow>
        data={data}
        columns={columns}
        serverSide
        total={total}
        from={from}
        to={to}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        defaultPageSize={10}
        pageSizeOptions={[10]}
        loading={loading}
        showActions={true}
        onDelete={handleDeleteClick}
        onAdd={() => {
          setEditingVehicle(null);
          setOpenModal(true);
        }}
        onEdit={async (row) => {
          setEditingVehicle(row);
          setOpenModal(true);
        }}
      />

      {/* Add / Edit Modal */}
      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={editingVehicle ? "Edit Vehicle" : "Add Vehicle"}
      >
        <FormBuilder
          ref={formRef}
          fields={getVehicleFields(!!editingVehicle)}
          onSubmit={editingVehicle ? handleUpdateVehicle : handleCreateVehicle}
          onCancel={handleCloseModal}
          submitLabel={editingVehicle ? "Update Vehicle" : "Add Vehicle"}
          error={formErrors}
          defaultValues={
            editingVehicle
              ? editingVehicle
              : buildDefaultValues(getVehicleFields(!!editingVehicle))
          }
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Delete"
      >
        <p className="mb-5 text-gray-700">
          Are you sure you want to delete{" "}
          <span className="font-semibold">
            {vehicleToDelete?.vehicle_type} ({vehicleToDelete?.model})?
          </span>
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => setDeleteModalOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default VehicleManagement;
